import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { ERROR_BASE_DATE } from '~/constants/organization_notification';
import {
  GetUnreadSendFaxErrorNotificationsDocument,
  MarkOrganizationNotificationDocument,
} from '~/graphql/generated/graphql';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { faximoSendFaxErrorState } from '~/state/layouts/SharedAppShell/atoms';
import { FaximoSendFaxErrorNotification } from '~/state/layouts/SharedAppShell/types';

export const useSubscriptionFaximoSendFax = () => {
  const [{ notifications, loaded }, setState] = useAtom(faximoSendFaxErrorState);
  const [event, clear] = useSubscriptions('OrganizationNotification', ['created', 'updated']);
  const [markNotification] = useMutation(MarkOrganizationNotificationDocument);
  const [getNotifications, { data, loading }] = useLazyQuery(
    GetUnreadSendFaxErrorNotificationsDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (loading) return;
    if (!data?.me || data.me.__typename !== 'Practitioner') return;

    const organizationNotifications = data.me.organization.unreadOrganizationNotifications.nodes;
    const notifications = organizationNotifications
      .filter((n) => !!n.faximoSendFax)
      .map((n) => {
        return {
          faximoSendFaxId: n.faximoSendFax?.id || '',
          organizationNotificationId: n.id,
          createdAt: n.createdAt,
        } as FaximoSendFaxErrorNotification;
      });

    setState((_state) => ({
      ..._state,
      notifications,
      loaded: true,
    }));
  }, [data, loading, setState]);

  const handleClose = useCallback(
    async (faximoSendFaxId: string) => {
      const newNotifications = notifications.filter(
        (notification) => notification.faximoSendFaxId !== faximoSendFaxId,
      );
      setState((_state) => ({ ..._state, notifications: newNotifications }));

      const notificationId = notifications.find(
        (n) => n.faximoSendFaxId === faximoSendFaxId,
      )?.organizationNotificationId;

      if (notificationId) {
        await markNotification({
          variables: {
            input: {
              organizationNotificationId: notificationId,
            },
          },
        });
      }
    },
    [markNotification, notifications, setState],
  );

  useEffect(() => {
    if (event) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
      clear();
    }
  }, [clear, event, getNotifications]);

  useEffect(() => {
    // ログイン時にエラー通知を取得する
    if (!loaded) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
    }
  }, [getNotifications, loaded]);

  return {
    handleClose,
  };
};
